import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {
  Button,
  Alert,
  AlertTypography,
  Skeleton,
  Typography,
  CircularProgress,
  Box,
} from '@ace-clublabs/shared-component-library';
import { IGetManifest, ErrorCode, PaymentType } from '@ace-clublabs/payment-types';
import PaymentMethodSelector, { PaymentMethodOption } from './PaymentMethodSelector';
import lock_icon from '../../assets/lock-icon.svg';
import { IGetAdyenSession, ISubmitAdyenTokenRequest } from '../../api/contracts';
import { Environments, getEnvironmentFromHost } from '../../api/endpoints';
import { sources } from '../../api/sources';
import { CardFormValidationHelper } from '../../utils/cardFormValidationHelper';
import { getManifestSoftErrorCode } from '../../utils/paymentNavigationHelper';
import { postToParent, isParentMessage, setParentOrigin, isOriginAllowed } from './embedPostMessage';
import { LogCannon } from '@ace-clublabs/log-cannon';
import { isApplePayAvailable, isMobileOrTablet } from '../../utils/applePayHelper';
import { UtagHelper } from '../../utils/utagHelper';
import './EmbedAdyenPayment.scss';

interface IAdyenEncryptedCardDataLocal {
  encryptedCardNumber: string;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
  encryptedSecurityCode?: string;
  holderName: string;
  postalCode: string;
}

interface IAdyenSessionData {
  sessionId: string;
  encryptedCardData: IAdyenEncryptedCardDataLocal;
}

export interface IEmbedAdyenPaymentProps {
  manifestId: string;
  manifest: IGetManifest;
  adyenSession: IGetAdyenSession;
}

const BILLING_ZIP_LENGTH_ERROR = 'ZIP code must be 5 characters.';
const BILLING_ZIP_REQUIRED_ERROR = 'Billing Zip is required.';

const FIELD_ERROR_MAP: Record<string, string> = {
  encryptedCardNumber: 'Card number is required.',
  encryptedExpiryDate: 'Card expiration is required.',
  encryptedSecurityCode: 'CVV is required.',
};

function showInlineError(container: HTMLElement, errorId: string, message: string): void {
  let errorEl = container.querySelector(`#${errorId}`) as HTMLElement | null;
  if (!errorEl) {
    errorEl = document.createElement('span');
    errorEl.id = errorId;
    errorEl.className = 'adyen-checkout-contextual-text adyen-checkout-contextual-text--error';
    errorEl.setAttribute('aria-live', 'polite');
    container.appendChild(errorEl);
  }
  errorEl.textContent = message;
}

function hideInlineError(errorId: string): void {
  document.getElementById(errorId)?.remove();
}

function injectFieldError(fieldType: string, message: string): void {
  const root = document.getElementById('embed-card-container');
  if (!root) return;
  const field = root
    .querySelector(`[data-cse="${fieldType}"]`)
    ?.closest('.adyen-checkout__field') as HTMLElement | null;
  if (!field) return;
  showInlineError(field, `embed-${fieldType}-error`, message);
}

function removeFieldError(fieldType: string): void {
  hideInlineError(`embed-${fieldType}-error`);
}

function getBillingZipLengthError(billing: { postalCode?: string; zipCode?: string } | undefined): string | null {
  const code = billing?.postalCode ?? billing?.zipCode;
  if (!code) return null;
  if (code.length !== 5) return BILLING_ZIP_LENGTH_ERROR;
  return null;
}

const { sanitizeBin } = (() => {
  const sanitizeBin = (raw: string): string => {
    if (raw.length < 6) return '';
    if (raw.length >= 8) return raw.substring(0, 8);
    return raw.substring(0, 6);
  };
  return { sanitizeBin };
})();

const EmbedAdyenPayment: React.FC<IEmbedAdyenPaymentProps> = ({ manifestId, manifest, adyenSession }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const adyenInitializedRef = useRef(false);
  const loadTagFiredRef = useRef(false);
  const softErrorHandledRef = useRef(false);

  const [cardComponent, setCardComponent] = useState<any>(null);
  const [adyenSessionData, setAdyenSessionData] = useState<IAdyenSessionData | null>(null);
  const [adyenCardDetails, setAdyenCardDetails] = useState<{ brand: string; last4: string; bin: string }>({
    brand: '',
    last4: '',
    bin: '',
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingZipFieldError, setBillingZipFieldError] = useState<string | null>(null);
  const [ctaText, setCtaText] = useState(manifest?.clientConfig?.iframeButtonLabel ?? 'Join AAA');
  const [showLabels, setShowLabels] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  const [applePayAvailable, setApplePayAvailable] = useState(false);

  const config = manifest?.clientConfig || {};

  const CARD_TYPES: PaymentType[] = [PaymentType.CreditOrDebit, PaymentType.DebitOnly, PaymentType.Prepaid];
  const WALLET_TYPES: PaymentType[] = [PaymentType.Apple, PaymentType.Google];

  const allowedPaymentTypesForSelector = config?.allowedPaymentTypes || [];
  const hasCardType = allowedPaymentTypesForSelector.some((t: PaymentType) => CARD_TYPES.includes(t));
  const hasWalletType = allowedPaymentTypesForSelector.some((t: PaymentType) => WALLET_TYPES.includes(t));
  const showSelector = hasCardType && hasWalletType && !manifest?.paymentType;

  const availableMethods: PaymentMethodOption[] = showSelector
    ? [
        ...(allowedPaymentTypesForSelector.includes(PaymentType.Apple)
          ? [{ id: 'apple', label: 'Apple Pay', type: 'apple-pay' as const, available: applePayAvailable }]
          : []),
        ...(allowedPaymentTypesForSelector.includes(PaymentType.Google)
          ? [{ id: 'google', label: 'Google Pay', type: 'google-pay' as const, available: googlePayAvailable }]
          : []),
        ...(hasCardType ? [{ id: 'card', label: 'Credit/debit card', type: 'card' as const, available: true }] : []),
      ]
    : [];

  useEffect(() => {
    isApplePayAvailable(config?.enableApplePayQRCode).then(setApplePayAvailable);
  }, [config?.enableApplePayQRCode]);

  // Fire initial payment-page tags once when manifest tags are available.
  useEffect(() => {
    if (!manifest?.tags || loadTagFiredRef.current) {
      return;
    }

    const { tags, clientConfig } = manifest;
    const { channel, subchannel1, subchannel2 } = tags || {};
    const currentPage = 'payment';
    const updatedTags = tags?.page
      ? UtagHelper.tagPage(tags, currentPage)
      : {
          ...tags,
          page: [channel, subchannel1, subchannel2, currentPage].filter(Boolean).join(':'),
        };

    const allowedPaymentTypes = clientConfig?.allowedPaymentTypes || [];
    const payment_options = [...allowedPaymentTypes].sort().join() || '';
    UtagHelper.tag({ ...updatedTags, payment_options });

    loadTagFiredRef.current = true;
  }, [manifest]);

  // Listen for CONFIGURE messages from parent
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isOriginAllowed(event.origin)) return;
      if (!isParentMessage(event.data)) return;

      setParentOrigin(event.origin);

      if (event.data.type === 'CONFIGURE') {
        const { ctaText: text } = event.data.payload || {};
        if (text) setCtaText(text);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // ResizeObserver for height reporting
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastHeight = 0;
    let debounceTimer: number;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const height = Math.ceil(entry.target.getBoundingClientRect().height);
      if (height && height !== lastHeight) {
        lastHeight = height;
        clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          postToParent('RESIZE', { height });
        }, 100);
      }
    });

    observer.observe(container);
    return () => {
      observer.disconnect();
      clearTimeout(debounceTimer);
    };
  }, []);

  // Check Google Pay readiness
  useEffect(() => {
    if (!showSelector || !allowedPaymentTypesForSelector.includes(PaymentType.Google)) return;
    let cancelled = false;
    let tries = 0;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    const googlePayEnv = getEnvironmentFromHost(window.location.hostname) === Environments.prod ? 'PRODUCTION' : 'TEST';

    const checkReady = async () => {
      const googleApi = (window as any).google;
      if (!googleApi?.payments?.api?.PaymentsClient) {
        if (!cancelled && tries++ < 20) timerId = setTimeout(checkReady, 250);
        return;
      }
      try {
        const paymentsClient = new googleApi.payments.api.PaymentsClient({ environment: googlePayEnv });
        const response = await paymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: 'CARD',
              parameters: {
                allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
              },
            },
          ],
        });
        if (!cancelled) setGooglePayAvailable(!!response?.result);
      } catch {
        if (!cancelled) setGooglePayAvailable(false);
      }
    };

    void checkReady();
    return () => {
      cancelled = true;
      if (timerId !== undefined) clearTimeout(timerId);
    };
  }, [showSelector, allowedPaymentTypesForSelector]);

  // Preselect Apple Pay when it becomes available
  useEffect(() => {
    if (!showSelector) return;
    if (!applePayAvailable) return;
    if (!allowedPaymentTypesForSelector.includes(PaymentType.Apple)) return;
    setSelectedPaymentMethod((prev) => prev ?? 'apple');
  }, [showSelector, applePayAvailable, allowedPaymentTypesForSelector]);

  // Preselect Google Pay once it becomes available (if Apple Pay is not available)
  useEffect(() => {
    if (!showSelector) return;
    if (!googlePayAvailable) return;
    if (applePayAvailable) return; // Apple has priority
    if (!allowedPaymentTypesForSelector.includes(PaymentType.Google)) return;
    setSelectedPaymentMethod((prev) => prev ?? 'google');
  }, [showSelector, googlePayAvailable, applePayAvailable, allowedPaymentTypesForSelector]);

  // Reset initialization ref when adyen session changes (supports StrictMode remount)
  useEffect(() => {
    adyenInitializedRef.current = false;
  }, [adyenSession]);

  // Initialize Adyen card component
  useEffect(() => {
    if (!adyenSession?.adyen?.clientKey) return;

    if (adyenInitializedRef.current) return;
    adyenInitializedRef.current = true;

    let isMounted = true;
    let card: any;

    const initializeAdyen = async () => {
      try {
        const translations = {
          'creditCard.holderName': 'Cardholder Name',
          'creditCard.expiryDate.label': 'Expiration Date',
          'creditCard.expiryDate.contextualText': 'Expiration Date',
          'creditCard.securityCode.label': 'CVV',
          zipCode: 'Billing Zip Code',
          postalCode: 'Billing Zip Code',
          'cc.num.900': 'Card number is required.',
          'cc.num.901': 'Card number is not valid.',
          'cc.num.902': 'Card number is not valid.',
          'cc.num.903': 'The card entered is not supported. Please try a different card.',
          'cc.dat.910': 'Card expiration is required.',
          'cc.dat.912': 'Card expiration is not valid.',
          'cc.dat.911': 'Card expiration is not valid.',
          'cc.cvc.920': 'CVV is required.',
          'cc.cvc.921': 'Enter the complete security code.',
        };

        const globalConfiguration = {
          session: {
            id: adyenSession.adyen.sessionId,
            sessionData: adyenSession.adyen.sessionData,
          },
          environment: adyenSession.adyen.environment,
          amount: { value: 0, currency: 'USD' },
          locale: 'en-US',
          countryCode: 'US',
          clientKey: adyenSession.adyen.clientKey,
          showPayButton: false,
          translations: {
            'en-US': { ...translations },
            en_US: { ...translations },
            en: { ...translations },
          },
          onError: (error: any) => {
            LogCannon.blast('Embed Adyen Error', { error, manifestId });
            if (manifest?.tags) {
              const paymentTags = UtagHelper.tagPage(manifest.tags, 'payment');
              paymentTags['error'] = 'blocked payment-unhandled_error';
              UtagHelper.link(paymentTags);
            }
            setError('An unexpected error occurred. Please try again.');
            postToParent('PAYMENT_ERROR', { errorCode: 'SystemError', message: 'Unexpected Adyen error' });
          },
        };

        const cardConfiguration = {
          billingAddressRequired: true,
          billingAddressMode: 'partial' as const,
          billingAddressAllowedCountries: ['US'],
          hasHolderName: true,
          holderNameRequired: true,
          autoFocus: false,
          maskSecurityCode: true,
          showPayButton: false,
          hideCVC: !!config.disableCVV,
          amount: { value: 0, currency: 'USD' },
          showContextualElement: false,
          placeholders: {
            holderName: 'John Doe',
            cardNumber: '1234 5678 9012 3456',
            expiryDate: '01/26',
            securityCodeThreeDigits: '123',
            securityCodeFourDigits: '1234',
          },
          onFieldValid: (event: any) => {
            if (event?.fieldType === 'encryptedCardNumber') {
              if (event.valid && event.endDigits) {
                setAdyenCardDetails((prev) => ({ ...prev, last4: event.endDigits }));
              } else if (!event.valid) {
                setAdyenCardDetails((prev) => ({ ...prev, last4: '' }));
              }

              const fullBin = event.issuerBin ? event.issuerBin.toString() : '';
              if (event.valid && fullBin.length >= 6) {
                setAdyenCardDetails((prev) => ({ ...prev, bin: sanitizeBin(fullBin) }));
              } else if (!event.valid) {
                setAdyenCardDetails((prev) => ({ ...prev, bin: '' }));
                setError(null);
              }
            }
          },
          onBinValue: (event: { binValue: string }) => {
            const candidate = event?.binValue || '';
            const newBin = sanitizeBin(candidate);
            if (!newBin) {
              setAdyenCardDetails((prev) => ({ ...prev, bin: '' }));
            } else {
              setAdyenCardDetails((prev) => {
                if (prev.bin.length === 8 && prev.bin.substring(0, 6) === newBin.substring(0, 6)) {
                  return prev;
                }
                return { ...prev, bin: newBin };
              });
            }
          },
          onBinLookup: (binLookupData: any) => {
            const supportedBrands = binLookupData?.supportedBrands || [];
            if (supportedBrands.length > 0) {
              const mappedBrand = CardFormValidationHelper.mapAdyenBrandToStandard(supportedBrands[0]);
              setAdyenCardDetails((prev) => ({ ...prev, brand: mappedBrand }));
            }
          },
          onFocus: (event: any) => {
            const fieldType = event?.fieldType;
            if (fieldType && FIELD_ERROR_MAP[fieldType]) {
              removeFieldError(fieldType);
            }
          },
          onBlur: (event: any) => {
            const fieldType = event?.fieldType;
            if (fieldType && FIELD_ERROR_MAP[fieldType] && event?.numChars === 0) {
              injectFieldError(fieldType, FIELD_ERROR_MAP[fieldType]);
            }
          },
          onChange: (state: any) => {
            const billingZipError = getBillingZipLengthError(state.data?.billingAddress);
            if (!billingZipError) {
              setBillingZipFieldError((prev) => (prev === BILLING_ZIP_REQUIRED_ERROR ? prev : null));
              setError((prev) => (prev === BILLING_ZIP_LENGTH_ERROR ? null : prev));
            }
            setIsFormValid(!!state.isValid && !billingZipError);

            if (state.isValid && state.data?.paymentMethod) {
              const paymentMethod = state.data.paymentMethod;
              const billingAddress = state.data.billingAddress || {};

              if (paymentMethod.encryptedCardNumber) {
                const encryptedCardData: IAdyenEncryptedCardDataLocal = {
                  encryptedCardNumber: paymentMethod.encryptedCardNumber,
                  encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
                  encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
                  holderName: paymentMethod.holderName || '',
                  postalCode: billingAddress.postalCode || '',
                };

                if (paymentMethod.encryptedSecurityCode) {
                  encryptedCardData.encryptedSecurityCode = paymentMethod.encryptedSecurityCode;
                }

                setAdyenSessionData({
                  sessionId: adyenSession.adyen.sessionId,
                  encryptedCardData,
                });
              }
            }
          },
          brands: config.adyenCardBrands ?? ['amex', 'diners', 'discover', 'jcb', 'mc', 'visa'],
          styles: {
            base: {
              color: '#3D4043',
              fontSize: '16px',
              fontFamily: 'Arial, sans-serif',
            },
            placeholder: {
              color: '#717182',
              fontSize: '14px',
              fontFamily: 'Arial, sans-serif',
            },
            error: {
              color: '#3d4043',
            },
          },
        };

        const checkout = await AdyenCheckout(globalConfiguration);
        if (!isMounted) return;

        card = new Card(checkout, cardConfiguration).mount('#embed-card-container');
        if (isMounted) setCardComponent(card);
      } catch (error) {
        LogCannon.blast('Failed to initialize embed Adyen card form', { error, manifestId });
        setError('Failed to load payment form. Please refresh to try again.');
        postToParent('PAYMENT_ERROR', { errorCode: 'InitError', message: 'Failed to initialize payment form' });
      }
    };

    initializeAdyen();

    return () => {
      isMounted = false;
      if (card) {
        try {
          card.unmount?.();
        } catch {
          // no-op
        }
      }
    };
  }, [adyenSession, config.disableCVV, config.adyenCardBrands, manifestId]);

  // Name on card validation (character restriction)
  useEffect(() => {
    if (!cardComponent) return;

    // Use document-level capture so the handler is ready before the holder input is inserted.
    const onInputCapture = (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (!target?.closest('.adyen-checkout__card__holderName')) return;
      const cleaned = target.value
        .replace(/[^a-zA-Z\s.'‘’\-]/g, '')
        .replace(/\s+/g, ' ')
        .slice(0, 26);
      if (cleaned !== target.value) {
        target.value = cleaned;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('.adyen-checkout__card__holderName')) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return;
      if (!/[a-zA-Z\s.'‘’\-]/.test(e.key)) e.preventDefault();
    };

    document.addEventListener('input', onInputCapture, true);
    document.addEventListener('keydown', onKeyDown, true);

    // Placeholder and blur/focus error display are applied once the input appears.
    const holderErrorId = 'embed-holder-name-field-error';
    const root = document.getElementById('embed-card-container') ?? document.body;
    let removeBlurFocus: (() => void) | undefined;

    const attachBlurFocus = (holderInput: HTMLInputElement) => {
      if (!holderInput.placeholder) {
        holderInput.placeholder = 'Full Name';
      }
      holderInput.maxLength = 26;
      const getFieldWrapper = () => holderInput.closest('.adyen-checkout__field') as HTMLElement | null;
      const onBlur = () => {
        const wrapper = getFieldWrapper();
        if (!holderInput.value.trim() && wrapper) {
          showInlineError(wrapper, holderErrorId, 'Cardholder Name is required.');
        }
      };
      const onFocus = () => hideInlineError(holderErrorId);
      holderInput.addEventListener('blur', onBlur);
      holderInput.addEventListener('focus', onFocus);
      removeBlurFocus = () => {
        holderInput.removeEventListener('blur', onBlur);
        holderInput.removeEventListener('focus', onFocus);
      };
    };

    const existing = root.querySelector('.adyen-checkout__card__holderName input') as HTMLInputElement | null;
    if (existing) {
      attachBlurFocus(existing);
    } else {
      const observer = new MutationObserver(() => {
        const holderInput = root.querySelector('.adyen-checkout__card__holderName input') as HTMLInputElement | null;
        if (!holderInput) return;
        observer.disconnect();
        attachBlurFocus(holderInput);
      });
      observer.observe(root, { childList: true, subtree: true });
      return () => {
        observer.disconnect();
        document.removeEventListener('input', onInputCapture, true);
        document.removeEventListener('keydown', onKeyDown, true);
        removeBlurFocus?.();
      };
    }

    return () => {
      document.removeEventListener('input', onInputCapture, true);
      document.removeEventListener('keydown', onKeyDown, true);
      removeBlurFocus?.();
    };
  }, [cardComponent]);

  // ZIP field validation (digits only, max 5)
  useEffect(() => {
    if (!cardComponent) return;

    let isUnmounted = false;
    const timeouts: number[] = [];

    const attachWhenReady = () => {
      if (isUnmounted) return;

      const root = document.getElementById('embed-card-container');
      const field = root?.querySelector(
        '.adyen-checkout__field--postalCode, .adyen-checkout__field--zipCode'
      ) as HTMLElement | null;
      const zipInput = field?.querySelector('input') as HTMLInputElement | null;
      if (!zipInput) {
        timeouts.push(window.setTimeout(attachWhenReady, 50));
        return;
      }

      try {
        zipInput.inputMode = 'numeric';
        zipInput.maxLength = 5;
        if (!zipInput.placeholder) {
          zipInput.placeholder = '12345';
        }
      } catch {
        // no-op
      }

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key.length !== 1) return;
        if (/\d/.test(e.key)) return;
        e.preventDefault();
      };

      const onInputCapture = (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        if (!target) return;
        const onlyDigits = target.value.replace(/\D/g, '').slice(0, 5);
        if (onlyDigits !== target.value) {
          target.value = onlyDigits;
        }
      };

      const onZipBlur = () => {
        if (!zipInput.value.trim()) {
          setBillingZipFieldError(BILLING_ZIP_REQUIRED_ERROR);
        } else {
          setBillingZipFieldError(getBillingZipLengthError({ postalCode: zipInput.value }));
        }
      };

      const onZipFocus = () => {
        setBillingZipFieldError(null);
      };

      zipInput.addEventListener('keydown', onKeyDown);
      zipInput.addEventListener('input', onInputCapture, true);
      zipInput.addEventListener('blur', onZipBlur);
      zipInput.addEventListener('focus', onZipFocus);

      return () => {
        zipInput.removeEventListener('keydown', onKeyDown);
        zipInput.removeEventListener('input', onInputCapture, true);
        zipInput.removeEventListener('blur', onZipBlur);
        zipInput.removeEventListener('focus', onZipFocus);
      };
    };

    const cleanup = attachWhenReady();

    return () => {
      isUnmounted = true;
      timeouts.forEach((t) => clearTimeout(t));
      if (typeof cleanup === 'function') cleanup();
    };
  }, [cardComponent]);

  useEffect(() => {
    const root = document.getElementById('embed-card-container');
    const field = root?.querySelector(
      '.adyen-checkout__field--postalCode, .adyen-checkout__field--zipCode'
    ) as HTMLElement | null;
    if (!field) return;

    const errorId = 'embed-zip-field-error';
    if (billingZipFieldError) {
      showInlineError(field, errorId, billingZipFieldError);
    } else {
      hideInlineError(errorId);
    }
  }, [billingZipFieldError, cardComponent]);

  // Move zip field into exp-cvc row and set CVV placeholder
  useEffect(() => {
    if (!cardComponent) return;

    let isUnmounted = false;
    const timeouts: number[] = [];

    const rearrange = () => {
      if (isUnmounted) return;

      const root = document.getElementById('embed-card-container');
      const expCvcRow = root?.querySelector('.adyen-checkout__card__exp-cvc') as HTMLElement | null;
      const zipField = root?.querySelector(
        '.adyen-checkout__field--postalCode, .adyen-checkout__field--zipCode'
      ) as HTMLElement | null;

      if (!expCvcRow || !zipField) {
        timeouts.push(window.setTimeout(rearrange, 50));
        return;
      }

      // Move zip into the exp-cvc row as the 3rd column
      expCvcRow.appendChild(zipField);
    };

    rearrange();

    return () => {
      isUnmounted = true;
      timeouts.forEach((t) => clearTimeout(t));
    };
  }, [cardComponent]);

  const handleSoftError = useCallback(
    (errorCode: ErrorCode) => {
      if (manifest?.tags) {
        const paymentTags = UtagHelper.tagPage(manifest.tags, 'payment');
        paymentTags['error'] = `blocked payment-${errorCode}`;
        UtagHelper.link(paymentTags);
      }
      switch (errorCode) {
        case ErrorCode.InvalidAccountHolderName:
          setError('Invalid name on the card.');
          break;
        case ErrorCode.InvalidExpirationDate:
          setError('Invalid expiration date.');
          break;
        case ErrorCode.InvalidCard:
        case ErrorCode.InvalidToken:
        case ErrorCode.InvalidInsuranceAutopayPaymentMethod:
        case ErrorCode.CardTypeNotAllowed:
        case ErrorCode.InvalidAccountNumber:
          setError('Invalid card number.');
          break;
        case ErrorCode.UnknownCardType:
        case ErrorCode.StandaloneAutoPayError:
        case ErrorCode.InvalidPaymentGatewayToken:
          setError('The card entered is not supported. Please try a different card.');
          break;
        case ErrorCode.ChargeDeclined:
          setError('Charge declined, please use a different card.');
          break;
        case ErrorCode.CreditCardNotAllowed:
          setError('Only debit cards are accepted, please try again.');
          break;
        case ErrorCode.DebitCardNotAllowed:
          setError('Debit cards are not accepted, please try again.');
          break;
        case ErrorCode.PrepaidCardNotAllowedForAutoPay:
        case ErrorCode.PrepaidCardNotAllowed:
          setError('Prepaid cards are not accepted, please try again.');
          break;
        case ErrorCode.CardMustBeAAAVisa:
          setError('Card must be a AAA Visa card.');
          break;
        case ErrorCode.InvalidZipCode:
          setError('Invalid billing ZIP code.');
          break;
        case ErrorCode.InvalidCVV:
          setError('Invalid CVV/security code.');
          break;
        case ErrorCode.InsufficientFunds:
          setError('Insufficient funds, please try again.');
          break;
        default:
          setError('There was an unexpected error. Please try again.');
          break;
      }
    },
    [manifest]
  );

  // Handle soft error code from manifest (runs when manifest changes; gated by ref to fire once)
  useEffect(() => {
    if (softErrorHandledRef.current) return;
    // Supports both { softError: { code } } and legacy { softErrorCode } shapes
    const softErrorCode = getManifestSoftErrorCode(manifest);
    if (softErrorCode) {
      softErrorHandledRef.current = true;
      handleSoftError(softErrorCode);
      postToParent('SOFT_ERROR', { errorCode: softErrorCode });
    }
  }, [manifest, handleSoftError]);

  const handleApplePayment = async () => {
    if (!(window as any).ApplePaySession) {
      setError('Apple Pay is not available on this device.');
      setIsSubmitting(false);
      return;
    }

    let walletSession: IGetAdyenSession | null = null;
    try {
      walletSession = await sources.getAdyenSession(manifestId, { paymentType: PaymentType.Apple });
    } catch (err) {
      LogCannon.blast('Embed: Failed to fetch Adyen session for Apple Pay', { err, manifestId });
      setError('Apple Pay is not available. Please try another payment method.');
      postToParent('PAYMENT_ERROR', { errorCode: 'ApplePaySessionError', message: 'Failed to fetch Adyen session' });
      setIsSubmitting(false);
      return;
    }

    const applePayAutoPay = manifest?.autoPay;
    const applePayProductType = manifest?.renderTsAndCs?.productType;
    const applePayProductLabel = applePayProductType ? `AAA ${applePayProductType} AutoPay` : 'AAA AutoPay';
    const applePayAmount = manifest?.amount;
    const request = {
      countryCode: 'US',
      currencyCode: 'USD',
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      requiredBillingContactFields: ['postalAddress', 'name'],
      total: {
        label: 'Payment',
        type: 'final',
        amount: manifest.amount.toFixed(2),
      },
      ...(applePayAutoPay && !isMobileOrTablet() && (window as any).ApplePaySession?.supportsVersion?.(16)
        ? {
            recurringPaymentRequest: {
              paymentDescription: applePayProductLabel,
              regularBilling: {
                label: applePayProductLabel,
                amount: applePayAmount && applePayAmount > 0 ? applePayAmount.toFixed(2) : '0',
                type: 'final',
                paymentTiming: 'recurring',
                recurringPaymentStartDate: new Date(),
                recurringPaymentIntervalUnit: 'year',
                recurringPaymentIntervalCount: 1,
              },
              managementURL: 'https://account.app.ace.aaa.com',
            },
          }
        : {}),
    };

    try {
      const applePayVersion =
        applePayAutoPay &&
        !config?.useDpan &&
        !isMobileOrTablet() &&
        (window as any).ApplePaySession?.supportsVersion?.(16)
          ? 16
          : 3;
      const appleSession = new (window as any).ApplePaySession(applePayVersion, request);
      appleSession.begin();

      appleSession.onvalidatemerchant = async (event: any) => {
        try {
          const merchantSession = await sources.authorizeApplePay('adyen', event?.validationURL);
          appleSession.completeMerchantValidation(merchantSession);
        } catch (err) {
          LogCannon.blast('Embed Apple Pay merchant validation failed', { err, manifestId });
          setError('Apple Pay could not be validated. Please try again.');
          postToParent('PAYMENT_ERROR', {
            errorCode: 'ApplePayValidationError',
            message: 'Merchant validation failed',
          });
          setIsSubmitting(false);
        }
      };

      appleSession.onpaymentauthorized = async (event: any) => {
        try {
          const { paymentData: cardData, paymentMethod } = event?.payment?.token || {};
          const { network: cardNetwork, displayName } = paymentMethod || {};
          const { givenName, familyName, postalCode: zipCode } = event.payment.billingContact || {};
          const cardHolderName = `${givenName || ''} ${familyName || ''}`.trim();

          const walletData = {
            walletType: 'applepay' as const,
            paymentMethodData: {
              type: 'applepay' as const,
              applePayToken: JSON.stringify(cardData),
            },
            cardInfo: {
              brand: cardNetwork || '',
              last4: displayName?.slice(-4) || '',
              holderName: cardHolderName,
              postalCode: zipCode || '',
            },
          };

          const tokenPayload: ISubmitAdyenTokenRequest = {
            adyen: {
              sessionId: walletSession!.adyen.sessionId,
              walletData,
            },
          };

          const response = await sources.submitAdyenToken(walletSession!.adyen.sessionId, tokenPayload);
          appleSession.completePayment({ status: (window as any).ApplePaySession.STATUS_SUCCESS });

          postToParent('PAYMENT_SUCCESS', {
            message: response.message,
            last4: response.last4 || walletData.cardInfo.last4,
            walletType: 'applepay',
            cardBrand: cardNetwork || '',
          });
          setIsComplete(true);
        } catch (err: any) {
          LogCannon.blast('Embed Apple Pay payment authorization failed', { err, manifestId });
          try {
            appleSession.completePayment({ status: (window as any).ApplePaySession.STATUS_FAILURE });
          } catch {
            // no-op
          }
          setError('Apple Pay payment failed. Please try again.');
          postToParent('PAYMENT_ERROR', {
            errorCode: err?.code || 'ApplePayError',
            message: err?.message || 'Apple Pay payment failed',
          });
        } finally {
          setIsSubmitting(false);
        }
      };

      appleSession.oncancel = () => {
        setIsSubmitting(false);
      };
    } catch (err: any) {
      LogCannon.blast('Embed Apple Pay session creation failed', { err, manifestId });
      setError('Apple Pay is not available. Please try another payment method.');
      postToParent('PAYMENT_ERROR', {
        errorCode: 'ApplePaySessionError',
        message: 'Failed to start Apple Pay session',
      });
      setIsSubmitting(false);
    }
  };

  const handleGooglePayment = async () => {
    let walletSession: IGetAdyenSession | null = null;
    try {
      walletSession = await sources.getAdyenSession(manifestId, { paymentType: PaymentType.Google });
    } catch (err) {
      LogCannon.blast('Embed: Failed to fetch Adyen session for Google Pay', { err, manifestId });
      setError('Google Pay is not available. Please try another payment method.');
      postToParent('PAYMENT_ERROR', { errorCode: 'GooglePaySessionError', message: 'Failed to fetch Adyen session' });
      setIsSubmitting(false);
      return;
    }

    const tokenizationSpecification = {
      type: 'PAYMENT_GATEWAY',
      parameters: {
        gateway: 'adyen',
        gatewayMerchantId: walletSession.adyen.merchantAccount,
      },
    };

    const paymentDataRequest: any = {
      apiVersion: 2,
      apiVersionMinor: 0,
      allowedPaymentMethods: [
        {
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
            billingAddressRequired: true,
            billingAddressParameters: { format: 'MIN' },
          },
          tokenizationSpecification,
        },
      ],
      merchantInfo: {
        merchantId: 'BCR2DN4TUOE3FKAL',
        merchantName: 'AAA Payments',
      },
      transactionInfo: {
        countryCode: 'US',
        currencyCode: 'USD',
        totalPriceStatus: 'FINAL',
        totalPrice: '0.00',
      },
      callbackIntents: ['PAYMENT_AUTHORIZATION'],
    };

    const onPaymentAuthorized = async (paymentData: any): Promise<any> => {
      try {
        const { name: cardHolderName, postalCode: zipCode } =
          paymentData?.paymentMethodData?.info?.billingAddress || {};
        const { tokenizationData, info } = paymentData.paymentMethodData || {};
        const { cardNetwork, cardDetails } = info || {};

        const walletData = {
          walletType: 'googlepay' as const,
          paymentMethodData: {
            type: 'googlepay' as const,
            googlePayToken: tokenizationData?.token,
            googlePayCardNetwork: cardNetwork,
          },
          cardInfo: {
            brand: cardNetwork || '',
            last4: cardDetails || '',
            holderName: cardHolderName || '',
            postalCode: zipCode || '',
          },
        };

        const tokenPayload: ISubmitAdyenTokenRequest = {
          adyen: {
            sessionId: walletSession!.adyen.sessionId,
            walletData,
          },
        };

        const response = await sources.submitAdyenToken(walletSession!.adyen.sessionId, tokenPayload);

        postToParent('PAYMENT_SUCCESS', {
          message: response.message,
          last4: response.last4 || walletData.cardInfo.last4,
          walletType: 'googlepay',
          cardBrand: cardNetwork || '',
        });
        setIsComplete(true);
        setIsSubmitting(false);
        return { transactionState: 'SUCCESS' };
      } catch (err: any) {
        LogCannon.blast('Embed Google Pay payment authorization failed', { err, manifestId });
        setError('Google Pay payment failed. Please try again.');
        postToParent('PAYMENT_ERROR', {
          errorCode: err?.code || 'GooglePayError',
          message: err?.message || 'Google Pay payment failed',
        });
        setIsSubmitting(false);
        return {
          transactionState: 'ERROR',
          error: {
            intent: 'PAYMENT_AUTHORIZATION',
            message: err?.message,
            reason: 'PAYMENT_DATA_INVALID',
          },
        };
      }
    };

    const _googlePayEnv =
      getEnvironmentFromHost(window.location.hostname) === Environments.prod ? 'PRODUCTION' : 'TEST';
    const paymentsClient = new (window as any).google.payments.api.PaymentsClient({
      environment: _googlePayEnv,
      paymentDataCallbacks: { onPaymentAuthorized },
    });

    paymentsClient.loadPaymentData(paymentDataRequest).catch((err: any) => {
      if (err.statusCode !== 'CANCELED' && err.name !== 'AbortError') {
        LogCannon.blast('Embed Google Pay loadPaymentData failed', { err, manifestId });
        setError('Google Pay payment failed. Please try again.');
        postToParent('PAYMENT_ERROR', { errorCode: 'GooglePayError', message: 'Google Pay payment failed' });
      }
      setIsSubmitting(false);
    });
  };

  const handleWalletSubmit = () => {
    if (isSubmitting || isComplete) return;
    setError(null);
    setIsSubmitting(true);
    if (selectedPaymentMethod === 'apple') {
      void handleApplePayment();
    } else if (selectedPaymentMethod === 'google') {
      void handleGooglePayment();
    } else {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || isComplete) return;
    setError(null);
    setIsSubmitting(true);
    cardComponent?.setStatus('loading');
    const submitStartedAt = Date.now();
    const minSpinnerMs = 400;

    const finishSubmitting = async () => {
      const elapsed = Date.now() - submitStartedAt;
      if (elapsed < minSpinnerMs) {
        await new Promise((resolve) => setTimeout(resolve, minSpinnerMs - elapsed));
      }
      cardComponent?.setStatus('ready');
      setIsSubmitting(false);
    };

    try {
      if (!cardComponent) {
        throw new Error('Card component not initialized');
      }

      const state = cardComponent.state;

      const billingZipError = getBillingZipLengthError(state.data?.billingAddress);
      if (billingZipError) {
        setBillingZipFieldError(billingZipError);
        await finishSubmitting();
        return;
      }
      setBillingZipFieldError(null);

      if (!state.isValid) {
        handleSoftError(ErrorCode.InvalidCard);
        await finishSubmitting();
        postToParent('VALIDATION_ERROR', { fields: ['form'] });
        return;
      }

      if (!adyenSessionData?.encryptedCardData) {
        throw new Error('Encrypted card data not available.');
      }

      const tokenZipError = getBillingZipLengthError({ postalCode: adyenSessionData.encryptedCardData.postalCode });
      if (tokenZipError) {
        setBillingZipFieldError(tokenZipError);
        await finishSubmitting();
        return;
      }

      const sessionId = adyenSessionData.sessionId;
      const payload: ISubmitAdyenTokenRequest = {
        adyen: {
          sessionId,
          cardData: {
            last4: adyenCardDetails.last4,
            cardBrand: adyenCardDetails.brand,
            cardHolderName: adyenSessionData.encryptedCardData.holderName,
            zipCode: adyenSessionData.encryptedCardData.postalCode,
            encryptedCardNumber: adyenSessionData.encryptedCardData.encryptedCardNumber,
            encryptedExpiryMonth: adyenSessionData.encryptedCardData.encryptedExpiryMonth,
            encryptedExpiryYear: adyenSessionData.encryptedCardData.encryptedExpiryYear,
            ...(adyenSessionData.encryptedCardData.encryptedSecurityCode && {
              encryptedSecurityCode: adyenSessionData.encryptedCardData.encryptedSecurityCode,
            }),
            bin: adyenCardDetails.bin || 'credit',
          },
        },
      };

      const response = await sources.submitAdyenToken(sessionId, payload);

      LogCannon.blast('Embed Adyen token submission successful', { response, manifestId });

      await finishSubmitting();

      postToParent('PAYMENT_SUCCESS', {
        message: response.message,
        last4: response.last4 || adyenCardDetails.last4,
        cardBrand: adyenCardDetails.brand,
        cardHolderName: adyenSessionData.encryptedCardData.holderName,
      });
      setIsComplete(true);
    } catch (error: any) {
      LogCannon.blast('Embed Adyen token submission failed', { error, manifestId });

      const errorCode = error?.code || 'SystemError';
      if (error?.code) {
        handleSoftError(error.code);
      } else {
        if (manifest?.tags) {
          const paymentTags = UtagHelper.tagPage(manifest.tags, 'payment');
          paymentTags['error'] = 'blocked payment-unhandled_error';
          UtagHelper.link(paymentTags);
        }
        setError('There was an unexpected error. Please try again.');
      }

      postToParent('PAYMENT_ERROR', {
        errorCode,
        message: error?.message || 'Payment submission failed',
      });

      await finishSubmitting();
    }
  };

  const labelClass = showLabels ? '' : 'embed-adyen-payment--no-labels';
  const submittingClass = isSubmitting ? 'embed-adyen-payment--submitting' : '';
  const completedClass = isComplete ? 'embed-adyen-payment--completed' : '';
  const isAdyenReady = !!cardComponent;

  const isWalletSelected = selectedPaymentMethod !== null && selectedPaymentMethod !== 'card';
  const cardContainerHidden = isAdyenReady && isWalletSelected;

  return (
    <div ref={containerRef} className={`embed-adyen-payment ${labelClass} ${submittingClass} ${completedClass}`}>
      <Box
        className="embed-payment-info-box"
        sx={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '14px', padding: '24px', mb: '30px' }}
      >
        {!isAdyenReady && (
          <div className="embed-adyen-payment-skeleton">
            {showSelector ? (
              <>
                <Skeleton className="embed-adyen-payment-skeleton__title" variant="rectangular" height={20} />
                <Skeleton className="embed-adyen-payment-skeleton__subtitle" variant="rectangular" height={20} />
                {availableMethods.map((_method, idx) => (
                  <Skeleton
                    key={idx}
                    className="embed-adyen-payment-skeleton__selector-row"
                    variant="rectangular"
                    height={56}
                  />
                ))}
                <Skeleton className="embed-adyen-payment-skeleton__button" variant="rectangular" height={48} />
              </>
            ) : (
              <>
                <Skeleton className="embed-adyen-payment-skeleton__title" variant="rectangular" height={32} />
                <Skeleton className="embed-adyen-payment-skeleton__subtitle" variant="rectangular" height={20} />
                <Skeleton className="embed-adyen-payment-skeleton__label" variant="rectangular" height={20} />
                <Skeleton className="embed-adyen-payment-skeleton__field--full" variant="rectangular" height={38} />
                <div className="embed-adyen-payment-skeleton__row">
                  <Skeleton className="embed-adyen-payment-skeleton__label--third" variant="rectangular" height={20} />
                  <Skeleton className="embed-adyen-payment-skeleton__label--third" variant="rectangular" height={20} />
                  <Skeleton className="embed-adyen-payment-skeleton__label--third" variant="rectangular" height={20} />
                </div>
                <div className="embed-adyen-payment-skeleton__row">
                  <Skeleton className="embed-adyen-payment-skeleton__field--third" variant="rectangular" height={38} />
                  <Skeleton className="embed-adyen-payment-skeleton__field--third" variant="rectangular" height={38} />
                  <Skeleton className="embed-adyen-payment-skeleton__field--third" variant="rectangular" height={38} />
                </div>
                <Skeleton className="embed-adyen-payment-skeleton__label" variant="rectangular" height={20} />
                <Skeleton className="embed-adyen-payment-skeleton__field--full" variant="rectangular" height={38} />
                <Skeleton className="embed-adyen-payment-skeleton__button" variant="rectangular" height={48} />
              </>
            )}
          </div>
        )}

        {isAdyenReady && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={lock_icon}
                alt="lock icon"
                className="lock-icon"
                style={{ marginRight: '8px', height: '20px', width: '20px' }}
              />
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  margin: '0',
                  color: '#0A0A0A',
                  fontSize: '16px',
                  fontWeight: '500',
                  fontFamily: ' Arial, sans-serif',
                }}
              >
                Payment Information
              </Typography>
            </Box>
            <Typography
              variant="b1Regular"
              sx={{
                fontSize: '16px',
                color: '#000',
                mb: '24px',
              }}
            >
              All transactions are secure and encrypted.
            </Typography>
          </>
        )}

        {error && (
          <Alert severity="error" id="embed-soft-error-alert" displayCloseButton={false} aria-live="polite">
            <AlertTypography>{error}</AlertTypography>
          </Alert>
        )}

        {isAdyenReady && showSelector && (
          <div className="payment-method-selector">
            <PaymentMethodSelector
              methods={availableMethods}
              selectedMethod={selectedPaymentMethod}
              onSelect={setSelectedPaymentMethod}
              cardBrands={config.adyenCardBrands}
            />
          </div>
        )}

        <div className="embed-adyen-payment__form">
          <div
            id="embed-card-container"
            aria-label="Payment card form"
            className={`embed-adyen-payment__card-container${cardContainerHidden ? ' embed-adyen-payment__card-container--hidden' : ''}`}
          ></div>
          {(isSubmitting || isComplete) && <div className="embed-adyen-payment__form-blocker" aria-hidden="true"></div>}
        </div>
      </Box>
      {isAdyenReady && (
        <div className="embed-adyen-payment__cta">
          <Button
            id="embed-submit-btn"
            fullWidth
            variant="contained"
            label={ctaText}
            onClick={isWalletSelected ? handleWalletSubmit : handleSubmit}
            disabled={isWalletSelected ? isSubmitting || isComplete : !isFormValid || isSubmitting || isComplete}
            aria-busy={isSubmitting}
            sx={{ borderRadius: '10px' }}
          />
          {isSubmitting && (
            <div className="embed-adyen-payment__cta-spinner" aria-hidden="true">
              <CircularProgress size={20} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmbedAdyenPayment;
